import React, { useCallback } from 'react';
import { action } from 'mobx';
import { observer } from 'mobx-react';
import { Modal } from 'ui/base/modal';
import { BooleanStore } from 'ui/base/input';
import { BackButton } from 'ui/base/button/back_button';
import { StandardPage } from 'ui/base/page/standard_page';
import { LinkButton } from 'ui/public/button';
import { CheckboxInput } from 'ui/standard/input/checkbox_input';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { WeekdayStore } from './weekday_store';
import baseStyles from './weekday_input.module.css';
import WeekdayInputMessages from './weekday_input_messages';
import { ButtonClickEvent } from 'ui/base/types';
import WeekdayMessages from 'assets/enums/weekdays/weekdays_messages';
import { Weekday, getShortWeekday } from 'assets/enums/weekdays/weekdays';

// Style declarations and themes
type WeekdayInputStyles = {
  box: string;
  placeholder: string;
  modalContainer: string;
  header: string;
  commit: string;
  toggleAll: string;
  inputRow: string;
  inputLabel: string;
};

const themedStyles: ThemedStyles<WeekdayInputStyles> = {
  [Theme.DAYLIGHT]: {
    box: baseStyles.daylightBox,
    placeholder: baseStyles.daylightPlaceholder,
    modalContainer: baseStyles.modalContainer,
    header: baseStyles.header,
    commit: baseStyles.daylightCommit,
    toggleAll: baseStyles.daylightToggleAll,
    inputRow: baseStyles.inputRow,
    inputLabel: baseStyles.inputLabel,
  },
  [Theme.MIDNIGHT]: {
    box: baseStyles.midnightBox,
    placeholder: baseStyles.midnightPlaceholder,
    modalContainer: baseStyles.modalContainer,
    header: baseStyles.header,
    commit: baseStyles.midnightCommit,
    toggleAll: baseStyles.midnightToggleAll,
    inputRow: baseStyles.inputRow,
    inputLabel: baseStyles.inputLabel,
  },
};

// A component to render a row in the modal
type WeekdayInputRowProps = {
  text: string;
  store: BooleanStore;
  styles: WeekdayInputStyles;
};

const WeekdayInputRow = observer(({ text, store, styles }: WeekdayInputRowProps) => {
  const toggleStore = useCallback(action(() => {
    store.value = !store.value;
  }), [store]);

  return (
    <div className={styles.inputRow}>
      <div className={styles.inputLabel} onClick={toggleStore}>
        {text}
      </div>
      <CheckboxInput store={store}/>
    </div>
  );
});

// The component that handles the actual modal
type WeekdayInputModalProps = {
  store: WeekdayStore;
};

const WeekdayInputModal = observer(({ store }: WeekdayInputModalProps) => {
  const styles = useStyles(themedStyles);

  const onCommit = React.useCallback(action((event: ButtonClickEvent) => {
    event.stopPropagation();
    store.commitWorkspace();
    store.modal.hideModal();
  }), [store]);

  // Note: toggleAll functionality can be abstracted away into a component that
  // takes a list of boolean stores as argument. We can do that once a uniform
  // 'styling' for this feature has been decided.
  const onToggleAll = React.useCallback(action((event: ButtonClickEvent) => {
    event.stopPropagation();
    const toCheckAll = !store.workspaceHasChecked;
    Object.values(store.workspace).forEach((booleanStore) => {
      booleanStore.value = toCheckAll;
    });
  }), [store]);

  return (
    <div className={styles.modalContainer}>
      <StandardPage>
        <div className={styles.header}>
          <BackButton onClick={store.modal.hideModal}/>
          <button type='button' className={styles.commit} onClick={onCommit}>
            {WeekdayInputMessages.ok()}
          </button>
        </div>
        <div className={styles.inputRow}>
          <LinkButton type='button' className={styles.toggleAll} onClick={onToggleAll}>
            {!store.workspaceHasChecked ? WeekdayInputMessages.checkAll() : WeekdayInputMessages.uncheckAll()}
          </LinkButton>
        </div>
        <WeekdayInputRow
          styles={styles}
          text={WeekdayMessages.mondayFull()}
          store={store.workspace[Weekday.MONDAY]}
        />
        <WeekdayInputRow
          styles={styles}
          text={WeekdayMessages.tuesdayFull()}
          store={store.workspace[Weekday.TUESDAY]}
        />
        <WeekdayInputRow
          styles={styles}
          text={WeekdayMessages.wednesdayFull()}
          store={store.workspace[Weekday.WEDNESDAY]}
        />
        <WeekdayInputRow
          styles={styles}
          text={WeekdayMessages.thursdayFull()}
          store={store.workspace[Weekday.THURSDAY]}
        />
        <WeekdayInputRow
          styles={styles}
          text={WeekdayMessages.fridayFull()}
          store={store.workspace[Weekday.FRIDAY]}
        />
        <WeekdayInputRow
          styles={styles}
          text={WeekdayMessages.saturdayFull()}
          store={store.workspace[Weekday.SATURDAY]}
        />
        <WeekdayInputRow
          styles={styles}
          text={WeekdayMessages.sundayFull()}
          store={store.workspace[Weekday.SUNDAY]}
        />
      </StandardPage>
    </div>
  );
});

// The component that handles the actual modal
type WeekdayInputBoxProps = {
  placeholder: string;
  store: WeekdayStore;
};

const WeekdayInputBox = observer(({ placeholder, store }: WeekdayInputBoxProps) => {
  const styles = useStyles(themedStyles);
  const onShowModal = React.useCallback(action(() => {
    store.reloadWorkspace();
    store.modal.showModal();
  }), [store]);

  if (store.arrayValue.length > 0) {
    return (
      <button type='button' className={styles.box} onClick={onShowModal}>
        {store.arrayValue.map((day) => getShortWeekday(day)).join(', ')}
      </button>
    );
  } else {
    return (
      <button type='button' className={styles.box} onClick={onShowModal}>
        <span className={styles.placeholder}>{placeholder}</span>
      </button>
    );
  }
});

export type WeekdayInputProps = {
  placeholder: string;
  store: WeekdayStore;
};

export const WeekdayInput = observer(({ placeholder, store }: WeekdayInputProps) => {
  return (
    <>
      <Modal store={store.modal}>
        <WeekdayInputModal store={store}/>
      </Modal>
      <WeekdayInputBox placeholder={placeholder} store={store}/>
    </>
  );
});
